import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Animated, Platform, StyleSheet, Text, View } from 'react-native';
import FieldBubble from './FieldBubble';
import FieldCalendar from './FieldCalendar';
import FieldSelectModal from './FieldSelectModal';
import FieldText from './FieldText';
import FieldTimeslotGroup from './FieldTimeslotGroup';
import { allStyles, textFontSize } from './FormStyles';
import { translate } from './FormUtils';

export const FieldType = {
    phone: 'PHONE',
    text: 'TEXT',
    barcode: 'BARCODE',
    photo: 'PHOTO',
    date: 'DATE',
    select: 'SELECT',
    calendar: 'CALENDAR',
    bubble: 'BUBBLE',
    selectModal: 'SELECT_MODAL',
    timeslotGroup: 'TIMESLOT_GROUP',
}

function renderField(props, index = 0, getValueByName, onChange, getItemsByName, getErrorByName, onBlur) {
    const zIndex = 100 - index
    if (Array.isArray(props)) {
        return (
            <View style={[styles.fieldRow, {zIndex}]}>
                {props.map((childProps, index) => {
                    const marginRight = index != props.length -1 ? 10 : 0;
                    return (
                        <View style={{flex:1, marginRight}}>
                            {renderField(childProps, zIndex, getValueByName, onChange, getItemsByName, getErrorByName, onBlur)}
                        </View>
                    );
                })}
            </View>
        )
    } else {
        const value = getValueByName(props.name);
        const error = getErrorByName(props.name);

        let field = <View/>;

        if (props.fieldType === FieldType.text) {
            field = <FieldText {...props} containerStyle={{zIndex}} value={value} onChange={onChange} error={error} onBlur={onBlur}/>
        }  else if (props.fieldType === FieldType.bubble) {
            field = <FieldBubble {...props} containerStyle={{zIndex}} value={value} onChange={onChange} getItemsByName={getItemsByName} error={error}/>
        } else if (props.fieldType === FieldType.calendar) {
            field = <FieldCalendar {...props} containerStyle={{zIndex}} value={value} onChange={onChange} error={error}/>
        } else if (props.fieldType === FieldType.selectModal) {
            field = <FieldSelectModal {...props} containerStyle={{zIndex}} value={value} onChange={onChange} getItemsByName={getItemsByName} error={error}/>
        } else if (props.fieldType === FieldType.timeslotGroup) {
            field = <FieldTimeslotGroup {...props} containerStyle={{zIndex}} value={value} onChange={onChange} error={error}/>
        }

        return (
            <View key={index} style={{zIndex}}>
                {field}
                {props.footerMessage ?
                    <Text style={styles.greyText}>{props.footerMessage}</Text>
                    : null
                }
            </View>
        )
    }
}

export const SimpleForm = forwardRef((props, ref) => {
    const { fields = [], values = {}, onChange = () => {}, getItemsByName = () => {}, onBlur = () => {} } = props;

    const [errors, setErrors] = useState({});

    const getValueByName = (name) => {
        return values[name];
    };

    const getErrorByName = (name) => {
        return errors[name];
    }

    useImperativeHandle(ref, () => ({
        isValidForm: () => {
            let errors = {};
            let isValid = true;

            const checkField = (field, input, index) => {
                const value = input ?? getValueByName(field.name);
                if (Array.isArray(value)) {
                    value.forEach((childValue, index) => {
                        checkField(field, childValue, index);
                    });
                } else if (field.required && typeof value === 'object' && Object.keys(value).length === 0) {
                    errors[field.name] = field.customWarming ?? `${translate(['FORM', 'REQUIRED'])}`;
                    isValid = false;
                } else if (field.required && !value) {
                    errors[field.name] = field.customWarming ?? `${translate(['FORM', 'REQUIRED'])}`;
                    isValid = false;
                } else if (value && Array.isArray(field.rules) && (!field.shouldMatch || field.shouldMatch[index])) {
                    for (let i = 0; i < field.rules.length; i++) {
                        const rule = field.rules[i];
                        if (rule.regex) {
                            const regex = new RegExp(rule.regex);
                            if (!regex.test(value)) {
                                errors[field.name] = rule.message ?? `${translate(['FORM', 'INVALID_INPUT'])}${field.title.toLowerCase()}`;
                                isValid = false;
                                break;
                            }
                        }
                    }
                }
            }

            fields.forEach((field) => {
                if (Array.isArray(field)) {
                    field.forEach((childField) => {
                        checkField(childField);
                    });
                } else {
                    checkField(field);
                }
            })

            setErrors(errors);
            return isValid;
        }
    }));

    const fadeAnim = useRef(new Animated.Value(0)).current;

    useEffect(() => {
        Animated.timing(fadeAnim, {
            toValue: 1,
            duration: 300
        }).start();
    }, []);

    return (
        <Animated.View
            style={{flex:1, opacity: fadeAnim}}
            behavior={Platform.OS === "ios" ? "padding" : "height"}
        >
            {fields.map((field, index) => renderField(field, index, getValueByName, onChange, getItemsByName, getErrorByName, onBlur))}
        </Animated.View>
    )
});

const styles = StyleSheet.create({
    ...allStyles,
    greyText: {
        color: '#9E9E9E',
        fontSize: textFontSize,
        marginTop: -4,
        marginBottom: 4,
    },
});
